import React, { FC, useEffect } from 'react';
import { DiscountCodeForm } from '@components/molecules/DiscountCodeForm';
import { ShoppingCartTotal } from '@components/molecules/ShoppingCartTotal';
import { ShoppingCartHeader } from '@components/molecules/ShoppingCartHeader';
import { SafePaymentsCard } from '@components/molecules/SafePaymentsCard';
import { Footer } from '@components/molecules/Footer';
import { Typography, TypographyAppearanceVariants, TypographyTagVariants } from '@components/atoms/Typography';
import { Modules, useModule } from '@hooks/useModule';
import { useCustomNavigate } from '@hooks/useCustomNavigate';
import { SITE_URLS } from '@constants';
import { ShoppingCartTotalData } from '@types';
import styles from './ShoppingCartContainer.module.css';

interface ShoppingCartContainerProps {
    step: number;
    title: string;
    action: JSX.Element | JSX.Element[];
    total?: ShoppingCartTotalData;
    children: string | JSX.Element | JSX.Element[];
}

export const ShoppingCartContainer: FC<ShoppingCartContainerProps> = ({ step, title, action, total, children }) => {
    const { navigate } = useCustomNavigate();
    const { isModuleActive } = useModule();

    const inCatalogMode = isModuleActive(Modules.catalogMode);

    // if marketplace is in catalog mode, none of the pages of this shopping cart is available
    useEffect(() => {
        if (inCatalogMode) {
            navigate(`/${SITE_URLS.home}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inCatalogMode]);

    return (
        <div className="page" data-testid="shopping-cart-container">
            <ShoppingCartHeader activeStep={step} />
            <div
                className={`d-flex flex-column flex-grow-1 ${styles['shopping-cart']} bg-secondary ${step === 1 ? (isModuleActive(Modules.coupon) ? styles['current-basket'] : '') : ''}`}>
                <div className="flex-grow-1 container px-3 py-4 pb-lg-5">
                    <div className="d-flex align-items-center mb-2 mb-md-4 gap-4 justify-content-between justify-content-lg-start">
                        <Typography
                            classes="mb-0"
                            tagVariant={TypographyTagVariants.h1}
                            appearanceLevel={TypographyAppearanceVariants.level3}>
                            {title}
                        </Typography>
                    </div>
                    <div className="row">
                        <div className={total ? 'col-12 col-lg-8' : 'col-12'}>
                            <div className={styles['shopping-cart-data-container']}>{children}</div>
                        </div>
                        {total ? (
                            <div className={`${styles['total-container']} col-12 col-lg-4`}>
                                {step === 1 && isModuleActive(Modules.coupon) ? <DiscountCodeForm /> : ''}
                                <ShoppingCartTotal
                                    showPointsBelowTotalOnMobile={step === 1}
                                    productsValue={total.productsValue}
                                    discount={total.promotion.discount}
                                    eligibleItems={total.promotion.eligibleItems}
                                    subtotal={total.subtotal}
                                    shipping={total.shipping}
                                    total={total.total}
                                    points={total.points}
                                    products={step === 1 ? [] : total.products}
                                    nProducts={total.nProducts}
                                />
                                <div className="order-summary-action-container">{action}</div>
                                {step === 1 && <SafePaymentsCard />}
                            </div>
                        ) : (
                            <div className="text-center mt-4 py-2">{action}</div>
                        )}
                    </div>
                </div>
                <hr className="d-none d-lg-block m-0" />
            </div>

            <Footer mode="normal" />
        </div>
    );
};
